import React, { lazy, Suspense, useState, useEffect } from 'react';
import { Toolbar, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Box, IconButton, CircularProgress } from '@mui/material';
import { menuItems, bottomMenuItems, appHighlightItems } from './MenuItems';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useKeycloak } from '@react-keycloak/web';
import { whiteLabelData } from './WhiteLabelData';
// Lazy load the No-Code AI App

export default function DrawerContent({ isCollapsed, onToggleCollapse }) {
  const { keycloak, initialized } = useKeycloak();
  const navigate = useNavigate();
  const location = useLocation();
  const [clientData, setClientData] = useState(null);

  useEffect(() => {
    // Function to get the subdomain from the hostname
    const getSubdomain = () => {
      const hostname = window.location.hostname;
      // If localhost, use a default value for testing
      //if (hostname === 'localhost') return 'baionity'; // Default for local testing
      
      const parts = hostname.split('.');
      if (parts.length >= 3) {
        return parts[0]; // Return the subdomain part
      }
      return null; // No subdomain found
    };

    // Find client data based on subdomain
    const subdomain = getSubdomain();
    const matchingClient = whiteLabelData.find(client => 
      client.domain.startsWith(subdomain) || client.client_id === subdomain
    );
    
    if (matchingClient) {
      setClientData(matchingClient);
    } else {
      // Fallback to default branding if no matching client
      setClientData({
        name: 'Piosphere',
        logo_url: '../../images/pv_white.png',
      });
    }
  }, []);

  const handleItemClick = (item) => {
    if (item.action === 'logout') {
      keycloak.logout();
      navigate('/');
    } else if (item.action && typeof item.action === 'function') {
      // Execute the action function directly
      item.action();
    } else if (item.path) {
      // Check if path is an external URL (starts with http:// or https://)
      if (item.path.startsWith('http://') || item.path.startsWith('https://')) {
        window.open(item.path, '_blank'); // Open external links in a new tab
      } else {
        navigate(item.path); // Navigate to internal routes using router
      }
    }
  };

  const renderMenuItems = (items) => (
    items.map((item) => {
      if (item.type === 'section') {
        return (
          <Box key={item.label} sx={{ px: { xs: 1.5, sm: 2 }, py: 1 }}>
            {!isCollapsed && (
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  letterSpacing: '0.1em',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                }}
              >
                {item.label}
              </Typography>
            )}
          </Box>
        );
      }

      return (
        <ListItem key={item.label} disablePadding sx={{ mb: { xs: 0.5, sm: 1 } }}>
          <ListItemButton
            onClick={() => handleItemClick(item)}
            sx={{
              borderRadius: 2,
              backgroundColor: location.pathname === item.path ? 'grey.800' : 'transparent',
              minHeight: { xs: 44, sm: 48 },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1, sm: 1.25 },
              '&:hover': {
                backgroundColor: location.pathname === item.path ? 'grey.700' : 'grey.900',
              }
            }}
          >
            <ListItemIcon sx={{ 
              color: '#50C878',
              minWidth: { xs: 36, sm: 40 },
              mr: { xs: 1, sm: 1.5 }
            }}>
              <item.icon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
            </ListItemIcon>
            {!isCollapsed && <ListItemText 
              primary={item.label} 
              sx={{
                '& .MuiListItemText-primary': {
                  fontSize: { xs: '0.85rem', sm: '1rem' },
                  fontWeight: location.pathname === item.path ? 600 : 400
                },
                ...(item.label === 'Agent Studio' && {
                  color: '#50C878',
                  fontWeight: 'normal',
                })
              }}
            />}
          </ListItemButton>
        </ListItem>
      );
    })
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      overflowX: 'hidden'
    }}>
      <Toolbar sx={{ 
        justifyContent: 'space-between',
        minHeight: { xs: 56, sm: 64 },
        px: { xs: 1.5, sm: 2 }
      }}>
        {!isCollapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img 
              src={clientData?.logo_url || "../../images/pv_white.png"} 
              alt={`${clientData?.name || "Piosphere"} Logo`} 
              style={{ 
                height: window.innerWidth < 600 ? '32px' : '40px',
                maxWidth: '120px',
                objectFit: 'contain'
              }} 
            />
            <Typography variant="h6" component="div" sx={{ 
              ml: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              fontWeight: 600
            }}>
              {clientData?.name || "Piosphere"}
            </Typography>
          </Box>
        )}
        <IconButton 
          onClick={onToggleCollapse} 
          size={window.innerWidth < 600 ? "small" : "medium"}
          sx={{ 
            ...(isCollapsed ? {
              ml: 0,
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)'
            } : {
              ml: 'auto',
              transform: 'translateX(+10%)'
            }),
            '& .MuiSvgIcon-root': {
              fontSize: { xs: '1.2rem', sm: '1.5rem' }
            }
          }}
        >
          {isCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Toolbar>
      <Divider />
      <List sx={{ 
        flexGrow: 1, 
        overflow: 'auto', 
        overflowX: 'hidden',
        px: { xs: 1, sm: 1.5 },
        py: { xs: 1, sm: 1.5 }
      }}>
        {renderMenuItems(menuItems)}
        <Box sx={{ 
          position: 'relative', 
          pt: { xs: 2, sm: 3 },
          my: { xs: 0.5, sm: 1 }
        }}>
          {!isCollapsed && (
            <Divider sx={{
              '&::before, &::after': {
                borderColor: 'primary.main',
              },
            }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  backgroundColor: 'background.paper',
                  px: 1,
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}
              >
                Highlights
              </Typography>
            </Divider>
          )}
        </Box>
        {renderMenuItems(appHighlightItems.filter(item => item.type !== 'section'))}
      </List>
      <Divider />
      <List sx={{ 
        overflowX: 'hidden',
        px: { xs: 1, sm: 1.5 },
        py: { xs: 1, sm: 1.5 }
      }}>
        {renderMenuItems(bottomMenuItems)}
      </List>
    </Box>
  );
}
